import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Backdrop,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  IconButton,
  Divider,
  FormControlLabel,
  Switch,
  Paper, TextField
} from '@material-ui/core';
import {Delete, Save, ArrowUpward, ArrowDownward, Edit} from '@material-ui/icons';
import { useParams, useHistory } from 'react-router';
import { makeStyles } from '@material-ui/core/styles';

import Spec from '../../../models/Spec';
import CourseWork from '../../../models/CourseWork';
import { useDialog } from '../../../context/Dialog';
import useSnackbar from '../../../hooks/useSnackbar';
import utils from '../../../libs/utils';
import Simple from './Simple';
import Advanced from './Advanced';
import Comments from './Comments';
import Settings from './Settings';

interface SpecPaperProps {
  // original spec reference object
  spec: Spec

  // existing spec list
  existingSpecList?: Spec[]

  // spec delete callback
  onDeleteSpec?(): void

  // spec update callback
  onSpecChanged?(spec: Spec): void

  // spec before save callback
  onBeforeSpecSave?(spec: Spec): void

  // space after save callback
  onSpecSaved?(spec: Spec): void

  // move up spec callback
  onMoveUp?(): void

  // move down spec callback
  onMoveDown?(): void

  // hide file switch flag
  hideFileSwitch?: boolean

  // hide save button flag
  hideSaveButton?: boolean

  // hide delete button flag
  hideDeleteButton?: boolean

  // loading flag
  isLoading?: boolean
}

const useStyles = makeStyles((theme) => ({
  paper: {
    overflow: 'hidden'
  },
  headerActions: {
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1.5)
    }
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
  titleField: {
    '& .MuiInputBase-root': {
      ...theme.typography.h6,
      color: 'inherit'
    }
  }
}));

const SpecPaper = (props: SpecPaperProps) => {
  const  {
    spec,
    existingSpecList = [],
    onDeleteSpec,
    onSpecChanged,
    onBeforeSpecSave,
    onSpecSaved,
    onMoveUp,
    onMoveDown,
    hideFileSwitch = false,
    hideSaveButton = false,
    hideDeleteButton = false,
    isLoading: _isLoading = false
  } = props;

  const classes = useStyles();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [specDraft, setSpecDraft] = useState<Spec>(new Spec());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [editTitle, setEditTitle] = useState<boolean>(false);

  const history = useHistory();
  const { assignment_id: assignmentID } = useParams();
  const { showMessage, renderSnackbar } = useSnackbar();

  useEffect(() => {
    const _spec = new Spec(JSON.parse(JSON.stringify(spec)));
    if (assignmentID) _spec.assignmentID = assignmentID;
    setSpecDraft(_spec);
    setInitialized(true);
  }, [spec]);

  useEffect(() => {
    if (initialized && onSpecChanged) onSpecChanged(specDraft);
  }, [specDraft]);

  const dialog = useDialog();

  // save spec
  const saveSpec = async () => {
    try {
      const confirmed = await dialog.confirm(`Do you want to save your changes to ${spec.id || spec.name}?`);
      if (!confirmed) return;

      setIsLoading(true);

      if (onBeforeSpecSave) onBeforeSpecSave(specDraft);

      const specExists = existingSpecList.some(a => a.id === spec.id);
      const idChanged = spec.id !== specDraft.id;

      const message = await (async (): Promise<string> => {
        let message: string = '';

        // spec does not exist yet
        if (!specExists) {
          ({ message } = await specDraft.save());
        }

        // spec exists but id didn't changed
        else if (specExists && !idChanged) {
          ({ message } = await specDraft.update());
        }

        // spec exists but id changed
        else if (specExists && idChanged) {
          ({ message } = await specDraft.save());
          await Spec.delete(spec.id);
        }

        // add spec to assignment id
        if (assignmentID) {
          ({ message } = await CourseWork.addSpec(assignmentID, specDraft.id));
        }

        return message;
      })();

      showMessage(message, 'success');
      if (onSpecSaved) onSpecSaved(new Spec(specDraft));
    } catch (e) {
      showMessage(utils.getRequestErrorMessage(e), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // delete spec
  const deleteSpec = async () => {
    try {
      const confirmed = await dialog.confirm((() => {
        if (assignmentID) return `Do you want to remove ${spec.id || spec.name} from assignment ${assignmentID}?`;
        return `Do you want to delete ${spec.id || spec.name}?`;
      })());
      if (!confirmed) return;

      setIsLoading(true);

      const message = await ((async (): Promise<string> => {
        if (assignmentID) {
          const { message } = await CourseWork.removeSpec(assignmentID, spec.id);
          return message;
        }

        const { message } = await Spec.delete(spec.id);
        return message;
      })());

      if (onDeleteSpec) onDeleteSpec();
      showMessage(message, 'success');
      if (!assignmentID) setTimeout(() => history.goBack());
    } catch (e) {
      showMessage(utils.getRequestErrorMessage(e), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // set file mode
  const setFileMode = (value: boolean) => {
    const _spec = new Spec(specDraft);
    if (value) {
      dialog.confirm('File submission mode will remove all the test cases. Do you want to continue?').then(confirmed => {
        if (!confirmed) return;
        _spec.data = [];
        setSpecDraft(_spec);
      });
    }
    else {
      _spec.addItem();
      setSpecDraft(_spec);
    }
  };

  return (
    <>
      <Paper className={classes.paper + ' mt-4'}>
        <Box px={2} py={1.5} className="secondary-bg white-text">
          <Grid container alignItems="center" justify="space-between">
            <Grid item className="d-flex align-items-center">
              <Box pr={1}>
                {editTitle ? (
                  <TextField
                    value={specDraft.id}
                    onChange={e => {
                      setSpecDraft((state) => {
                        const _spec = new Spec(state);
                        _spec.id = e.target.value;
                        return _spec;
                      });
                    }}
                    className={classes.titleField}
                    autoFocus
                  />
                ) : (<Typography variant="h6">{specDraft.id}</Typography>)}
              </Box>

              <IconButton size="small" color="inherit" onClick={() => setEditTitle(!editTitle)}>
                <Edit fontSize="inherit" />
              </IconButton>
            </Grid>

            <Grid item className={classes.headerActions}>
              {!!onMoveUp && (
                <IconButton
                  size="small"
                  color="inherit"
                  title="Move Up"
                  onClick={onMoveUp}>
                  <ArrowUpward />
                </IconButton>
              )}

              {!!onMoveDown && (
                <IconButton
                  size="small"
                  color="inherit"
                  title="Move Down"
                  onClick={onMoveDown}>
                  <ArrowDownward />
                </IconButton>
              )}

              {!hideFileSwitch && (
                <FormControlLabel
                  label="File"
                  control={
                    <Switch
                      color="primary"
                      checked={!specDraft.data.length}
                      onChange={() => setFileMode(!!specDraft.data.length)}
                    />
                  }
                />
              )}

              {!hideSaveButton && (
                <IconButton
                  size="small"
                  color="inherit"
                  title="Save"
                  onClick={saveSpec}>
                  <Save />
                </IconButton>
              )}

              {!hideDeleteButton && (
                <IconButton
                  size="small"
                  color="inherit"
                  title="Delete"
                  onClick={deleteSpec}>
                  <Delete />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Tabs
            value={activeTab}
            onChange={(event, value) => setActiveTab(value)}
            indicatorColor="primary"
            textColor="primary"
            centered>
            <Tab label="SIMPLE" />
            <Tab label="ADVANCED" />
            <Tab label="COMMENTS" />
            <Tab label="SETTINGS" />
          </Tabs>
          <Divider />
        </Box>

        <Box p={2}>
          {[
            <Simple spec={specDraft} onSpecChange={setSpecDraft} />,
            <Advanced spec={specDraft} onSpecChange={setSpecDraft} />,
            <Comments spec={specDraft} onSpecChange={setSpecDraft} />,
            <Settings spec={specDraft} onSpecChange={setSpecDraft} />
          ][activeTab]}
        </Box>
      </Paper>

      <Backdrop
        className={classes.backdrop}
        open={isLoading || _isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      { renderSnackbar() }
    </>
  );
};

export default SpecPaper;